import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AddUserDetails } from '../types/addUserType';
import { addUser } from '../api/addUserApiService';
import { ADMIN_ERROR_MESSAGES } from '../errors';
import { translateRole } from '@/shared/utils/roleTranslation';

export const ERROR_MESSAGES = {
  REQUIRED: ADMIN_ERROR_MESSAGES.REQUIRED,
  EMAIL_INVALID_FORMAT: ADMIN_ERROR_MESSAGES.EMAIL_INVALID_FORMAT,
  PHONE_MIN_LENGTH: ADMIN_ERROR_MESSAGES.PHONE_MIN_LENGTH,
  ZIP_INVALID_FORMAT: ADMIN_ERROR_MESSAGES.ZIP_INVALID_FORMAT,
  EMAIL_ALREADY_EXISTS: ADMIN_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS,
} as const;

const validateName = (name: string): string => {
  if (!name.trim()) {
    return ERROR_MESSAGES.REQUIRED;
  }
  return '';
};

const validateEmail = (email: string): string => {
  if (!email.trim()) {
    return ERROR_MESSAGES.REQUIRED;
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) {
    return ERROR_MESSAGES.EMAIL_INVALID_FORMAT;
  }
  return '';
};

const validatePhone = (phone: string): string => {
  if (!phone.trim()) {
    return ERROR_MESSAGES.REQUIRED;
  }
  const digitsOnly = phone.replace(/\D/g, '');
  if (digitsOnly.length < 9) {
    return ERROR_MESSAGES.PHONE_MIN_LENGTH;
  }
  return '';
};

const validateAddress = (address: string): string => {
  if (!address.trim()) {
    return ERROR_MESSAGES.REQUIRED;
  }
  return '';
};

const validateCity = (city: string): string => {
  if (!city.trim()) {
    return ERROR_MESSAGES.REQUIRED;
  }
  return '';
};

const validateZip = (zip: string): string => {
  // Polish zip code format: XX-XXX (5 digits)
  const zipRegex = /^\d{2}-\d{3}$/;
  if (!zipRegex.test(zip.trim()) && zip.trim().length) {
    return ERROR_MESSAGES.ZIP_INVALID_FORMAT;
  }
  return '';
};

const validateState = (state: string): string => {
  if (!state.trim()) {
    return ERROR_MESSAGES.REQUIRED;
  }
  return '';
};

export interface UseAddUserReturn {
  // Form data
  formData: AddUserDetails;

  // Errors
  errors: AddUserDetails;

  // Submission state
  isSubmitting: boolean;

  // Snackbar state
  snackbarError: string;
  isSnackbarOpen: boolean;
  handleCloseSnackbar: () => void;

  // Modal state
  isCancelModalOpen: boolean;
  handleOpenCancelModal: () => void;
  handleCloseCancelModal: () => void;
  handleConfirmCancel: () => void;

  // Field change handlers (union type for TextField onChange compatibility)
  handleNameChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleEmailChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handlePhoneChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleAddress1Change: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleAddress2Change: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleCityChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleStateChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleZipChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;

  // Form handlers
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  handleCancel: () => void;
}

const initialFormData: AddUserDetails = {
  name: '',
  email: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: 'Polska',
  role: translateRole('ADMIN'),
};

const initialErrors: AddUserDetails = {
  name: '',
  email: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  role: '',
};

export const useAddUser = (): UseAddUserReturn => {
  const navigate = useNavigate();
  // State
  const [formData, setFormData] = useState<AddUserDetails>(initialFormData);
  const [errors, setErrors] = useState<AddUserDetails>(initialErrors);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [snackbarError, setSnackbarError] = useState<string>('');
  const [isSnackbarOpen, setIsSnackbarOpen] = useState<boolean>(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);

  // Field change handlers
  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trimStart();
      setFormData((prev) => ({ ...prev, name: value }));
      if (errors.name) {
        setErrors((prev) => ({ ...prev, name: '' }));
      }
    },
    [errors.name]
  );

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.replace(/\s/g, '');
      setFormData((prev) => ({ ...prev, email: value }));
      if (errors.email) {
        setErrors((prev) => ({ ...prev, email: '' }));
      }
    },
    [errors.email]
  );

  const PHONE_MAX_LENGTH = 9;

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trim().slice(0, PHONE_MAX_LENGTH);
      setFormData((prev) => ({ ...prev, phone: value }));
      if (errors.phone) {
        setErrors((prev) => ({ ...prev, phone: '' }));
      }
    },
    [errors.phone]
  );

  const handleAddress1Change = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trimStart();
      setFormData((prev) => ({ ...prev, address1: value }));
      if (errors.address1) {
        setErrors((prev) => ({ ...prev, address1: '' }));
      }
    },
    [errors.address1]
  );

  const handleAddress2Change = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trimStart();
      setFormData((prev) => ({ ...prev, address2: value }));
    },
    []
  );

  const handleCityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trim();
      setFormData((prev) => ({ ...prev, city: value }));
      if (errors.city) {
        setErrors((prev) => ({ ...prev, city: '' }));
      }
    },
    [errors.city]
  );

  const handleStateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trimStart();
      setFormData((prev) => ({ ...prev, state: value }));
      if (errors.state) {
        setErrors((prev) => ({ ...prev, state: '' }));
      }
    },
    [errors.state]
  );

  const handleZipChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trim();
      setFormData((prev) => ({ ...prev, zip: value }));
      if (errors.zip) {
        setErrors((prev) => ({ ...prev, zip: '' }));
      }
    },
    [errors.zip]
  );

  // Form submission handler
  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      // Validate all fields
      const validationErrors: AddUserDetails = {
        name: validateName(formData.name),
        email: validateEmail(formData.email),
        phone: validatePhone(formData.phone),
        address1: validateAddress(formData.address1),
        address2: '', // Address2 is optional
        city: validateCity(formData.city),
        state: validateState(formData.state),
        zip: validateZip(formData.zip),
        country: '', // Country is disabled/pre-filled
        role: '', // Role is disabled/pre-filled
      };

      // Set all errors at once
      setErrors(validationErrors);

      // Check if there are any errors
      const hasErrors = Object.values(validationErrors).some(
        (error) => error !== ''
      );
      if (!hasErrors) {
        // Handle form submission - all validations passed
        setIsSubmitting(true);
        try {
          const result = await addUser(formData);
          if (result.success) {
            // Navigate to administrators list with success state
            navigate('/administrators', {
              replace: true,
              state: { showAddUserSuccess: true },
            });
          } else {
            // Handle error
            if (result.error === ERROR_MESSAGES.EMAIL_ALREADY_EXISTS) {
              // Show email error inline
              setErrors((prev) => ({
                ...prev,
                email: result.error || '',
              }));
            } else {
              // Show other errors in snackbar
              setSnackbarError(
                result.error || ADMIN_ERROR_MESSAGES.DEFAULT_ERROR
              );
              setIsSnackbarOpen(true);
            }
            console.error('Error:', result.error);
            setIsSubmitting(false);
          }
        } catch (error) {
          console.error('Unexpected error:', error);
          // Show unexpected error in snackbar
          setSnackbarError(ADMIN_ERROR_MESSAGES.UNEXPECTED_ERROR);
          setIsSnackbarOpen(true);
          setIsSubmitting(false);
        }
      }
    },
    [formData, navigate]
  );

  // True only if the user has typed into any editable field
  const hasContent = Boolean(
    formData.name ||
    formData.email ||
    formData.phone ||
    formData.address1 ||
    formData.address2 ||
    formData.city ||
    formData.state ||
    formData.zip
  );

  // Cancel handler - opens modal only when the form has user input
  const handleCancel = useCallback(() => {
    if (!hasContent) {
      navigate('/administrators', { replace: true });
      return;
    }
    setIsCancelModalOpen(true);
  }, [hasContent, navigate]);

  // Modal handlers
  const handleOpenCancelModal = useCallback(() => {
    setIsCancelModalOpen(true);
  }, []);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  // Confirm cancel - actually cancels and navigates away
  const handleConfirmCancel = useCallback(() => {
    // Reset form to initial state
    setFormData(initialFormData);
    setErrors(initialErrors);
    setIsSnackbarOpen(false);
    setSnackbarError('');
    setIsCancelModalOpen(false);
    // Navigate back to administrators list
    navigate('/administrators', { replace: true });
  }, [navigate]);

  // Snackbar close handler
  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
    setSnackbarError('');
  }, []);

  return {
    formData,
    errors,
    isSubmitting,
    snackbarError,
    isSnackbarOpen,
    handleCloseSnackbar,
    handleNameChange,
    handleEmailChange,
    handlePhoneChange,
    handleAddress1Change,
    handleAddress2Change,
    handleCityChange,
    handleStateChange,
    handleZipChange,
    handleSubmit,
    handleCancel,
    isCancelModalOpen,
    handleOpenCancelModal,
    handleCloseCancelModal,
    handleConfirmCancel,
  };
};
